import type { ReactNode } from "react";
import { formatCount } from "@shared-core/domain/formatters";
import { formatPriceOrNotYetPriced } from "@shared-core/domain/checkout-delivery";
import { Card } from "../../app/components/primitives/card";
import { CalendarClock } from "lucide-react";
import { KitPlaces, type KitPlacesView, type LoadKitDrops } from "./KitPlaces";
import type { CustomerOrderConsignment } from "@shared-core/data/customer-api-types";
import { ServedParcelUpdates } from "../portal/order-detail/OrderParcelEvents";
import { ParcelTracking } from "./ParcelTracking";

// Where each item goes, grouped by address, the same on the quote and the
// order page (Ben, 5 Oct). A quote says how long after approval; an order
// says where each delivery has got to, and under each address one block per
// consignment sent there, with its tracking link (order to invoice doc 102
// §9.5). The Delivery tab is the one place an order's parcels are followed
// (customer delivery updates): each block says when it arrives or did, and its
// milestones where the server gives them. Every sentence here is the server's;
// this only draws them. The Tharstern-era order serves the same shape and reads
// each consignment's progress on its own (P05), so it draws that under the link.

/** The read's delivery group (the quote's and the order's share the shape). */
export interface DeliveryGroupView {
  key: string;
  address_name: string;
  address_lines: string[];
  rows: Array<{
    ref: string;
    title: string;
    version: string | null;
    /** Null on a quote line still offering quantities. */
    quantity: number | null;
    status: string;
    /** An order's, while only part has gone: "{x} of {y} despatched so far". */
    despatched_so_far?: number | null;
  }>;
  /** An order's: each parcel or pallet run to this address. */
  consignments?: CustomerOrderConsignment[];
}

/** A delivery service line: its cost (Ben, 5 Oct: service lines are not
 *  items, so delivery's detail lives here and its price on Price). */
export interface DeliveryCharge {
  ref: string;
  title: string;
  spec: string;
  /** Null: not yet priced (order to invoice doc 104 §5.6), never £0. */
  price_pence: number | null;
  vat_label: string | null;
  /** Where it goes, when the goods go to several places (order to invoice doc 106 §4). */
  delivers_to?: string | null;
}

export function DeliveryByAddress({
  groups,
  sentence,
  charges = [],
  kits = [],
  loadKitDrops = null,
  renderProgress = null,
}: {
  groups: DeliveryGroupView[];
  sentence: string | null;
  charges?: DeliveryCharge[];
  /** Kit lines (V1 completion R5): counted in a sentence each, their places read a page at a time. */
  kits?: KitPlacesView[];
  loadKitDrops?: LoadKitDrops | null;
  /**
   * Where the page reads a consignment's progress itself (a Tharstern-era order: each consignment with a `key` is its
   * own lazy read), what goes under its link in place of the served milestones. Null: the served ones.
   */
  renderProgress?:
    ((consignment: CustomerOrderConsignment) => ReactNode) | null;
}) {
  return (
    <div className="space-y-4">
      {sentence && (
        <p className="flex items-center gap-2 text-sm font-medium">
          <CalendarClock className="size-4 shrink-0 text-muted-foreground" />
          {sentence}
        </p>
      )}
      {charges.length > 0 && (
        <Card className="gap-2 p-5">
          <p className="font-medium">Delivery charge</p>
          <ul className="space-y-1 text-sm">
            {charges.map((c) => (
              <li key={c.ref} className="flex justify-between gap-3">
                <span>
                  <span className="text-muted-foreground tabular-nums">
                    {c.ref}
                  </span>{" "}
                  {c.spec ? `${c.title}, ${c.spec}` : c.title}
                  {c.delivers_to && (
                    <span className="block text-xs text-muted-foreground">
                      To {c.delivers_to}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  {formatPriceOrNotYetPriced(c.price_pence)}
                  {c.price_pence !== null && c.vat_label && (
                    <span className="block text-xs text-muted-foreground">
                      {c.vat_label}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {kits.map((kit) => (
        <KitPlaces key={kit.ref} kit={kit} load={loadKitDrops} />
      ))}
      {groups.map((g) => {
        // One parcel to the address says its day once, in the parcel: a row repeating it is left out.
        const soleDay =
          g.consignments?.length === 1 ? g.consignments[0].day : null;
        return (
          <Card key={g.key} className="gap-3 p-5">
            <div>
              <p className="font-medium">{g.address_name}</p>
              <p className="text-sm text-muted-foreground">
                {g.address_lines.join(", ")}
              </p>
            </div>
            <ul className="divide-y">
              {g.rows.map((r, i) => (
                <li
                  key={`${r.ref}-${r.version ?? ""}-${i}`}
                  className="flex flex-wrap items-start justify-between gap-2 py-2.5 text-sm"
                >
                  <span>
                    <span className="text-muted-foreground tabular-nums">
                      {r.ref}
                    </span>{" "}
                    {r.title}
                    {r.version && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {r.version}
                      </span>
                    )}
                    {r.quantity !== null && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {r.quantity.toLocaleString("en-GB")}
                      </span>
                    )}
                  </span>
                  <span className="text-right text-muted-foreground">
                    {r.status === soleDay ? null : r.status}
                    {r.despatched_so_far != null && r.quantity !== null && (
                      <span className="block text-xs tabular-nums">
                        {formatCount(r.despatched_so_far)} of{" "}
                        {formatCount(r.quantity)} despatched so far
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {(g.consignments ?? []).map((c, i, all) => (
              <ParcelTracking
                key={`${c.tracking_reference ?? ""}-${c.despatched_on}-${i}`}
                title={
                  all.length > 1 ? `Parcel ${i + 1} of ${all.length}` : null
                }
                day={c.day}
                dayTone={c.day_tone}
                carrier={c.carrier}
                boxes={c.boxes}
                trackingReference={c.tracking_reference}
                trackingUrl={c.tracking_url}
                // A missed delivery the customer can sort out with the carrier: its page first. A delay staff are
                // looking at first shows no milestones and keeps the link quiet.
                isLinkProminent={
                  c.day_tone === "attention" &&
                  c.progress?.events[0]?.kind === "delivery_attempted"
                }
              >
                {renderProgress && c.key ? (
                  renderProgress(c)
                ) : c.progress ? (
                  <ServedParcelUpdates progress={c.progress} foldAfter={2} />
                ) : null}
              </ParcelTracking>
            ))}
          </Card>
        );
      })}
    </div>
  );
}
